// /app/frontend/src/components/admin/FabricCollectionsTab.jsx
// Admin UI for managing fabric brands, collections, materials, and colors
// REFACTORED: Uses sub-components from ./fabric/ for presentation

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Loader2, ExternalLink, Search, ClipboardList, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

// Import refactored sub-components
import BrandsTreePanel from './fabric/BrandsTreePanel';
import CollectionPanel from './fabric/CollectionPanel';
import { ColorImage } from './fabric/ColorImage';
import { GenerateRoomsDialog } from './fabric/GenerateRoomsDialog';
import { RegenerateJobBanner } from './fabric/RegenerateJobBanner';
import { TextureReplacePicker } from './fabric/TextureReplacePicker';
import { BulkMatchDialog } from './fabric/BulkMatchDialog';
import {
  BrandDialog, CollectionDialog, ColorDialog, MaterialDialog, ImagePreviewDialog, DeleteConfirmDialog,
  CsvImportDialog, BulkPriceDialog, BulkCsvPriceDialog, TemplateDialog, PriceHistoryDialog, AuditResultsDialog, ColorCompareDialog,
  MergeMaterialsDialog, SplitMaterialDialog, StockReportDialog, WeeklyResyncDialog,
} from './fabric/dialogs';
import { ResyncJobBanner } from './fabric/ResyncJobBanner';
import { PublicPriceToggle } from './fabric/PublicPriceToggle';
import { useCatalogResync } from '../../hooks/useCatalogResync';
import { parseColorsCSV, parseMaterialsCSV } from './fabric/csvParsers';
import { useVisualizationJob } from '../../hooks/useVisualizationJob';
import { DEFAULT_ROOM_TYPE } from '../../lib/roomTypes';
import { tierLabels, stockBadge, DISCONTINUED } from '../../lib/fabricLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FabricCollectionsTab = ({ token, jumpTo }) => {
  // State
  const [brands, setBrands] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [expandedBrands, setExpandedBrands] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [globalSearching, setGlobalSearching] = useState(false);
  const globalSearchRef = useRef(null);
  
  // Modals
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [materialImportOpen, setMaterialImportOpen] = useState(false);
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkCsvOpen, setBulkCsvOpen] = useState(false);
  const [bulkCsvFile, setBulkCsvFile] = useState(null);
  const [bulkCsvResult, setBulkCsvResult] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  
  // Edit state
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [editingColor, setEditingColor] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mergeIds, setMergeIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [splitReq, setSplitReq] = useState(null); // { material, colorIds }
  const [stockReportOpen, setStockReportOpen] = useState(false);
  const [weeklyResyncOpen, setWeeklyResyncOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // CSV Import state
  const [csvData, setCsvData] = useState('');
  const [csvPreview, setCsvPreview] = useState([]);
  
  // Material import state
  const [materialCsvData, setMaterialCsvData] = useState('');
  const [materialCsvPreview, setMaterialCsvPreview] = useState([]);
  
  // Material search/filter
  const [materialSearch, setMaterialSearch] = useState('');
  
  // Bulk price update state
  const [pricePercentage, setPricePercentage] = useState(0);
  const [priceScope, setPriceScope] = useState('collection');
  const [priceMode, setPriceMode] = useState('percentage');
  const [flatPrice, setFlatPrice] = useState(0);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Price History state
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Auto-generate visualization state
  const [autoGenerateViz, setAutoGenerateViz] = useState(true);
  const [vizRoomType, setVizRoomType] = useState(DEFAULT_ROOM_TYPE);
  const [roomsDialogCollection, setRoomsDialogCollection] = useState(null);
  const { job: roomsJob, isRunning: roomsJobRunning, dismissJob: dismissRoomsJob, adoptJob } = useVisualizationJob(token);
  const [generatingViz, setGeneratingViz] = useState(false);

  // Texture Replace state
  const [textureReplaceOpen, setTextureReplaceOpen] = useState(false);
  const [textureReplaceColor, setTextureReplaceColor] = useState(null);

  // Bulk Match state
  const [bulkMatchOpen, setBulkMatchOpen] = useState(false);

  // Global Color Search (debounced)
  useEffect(() => {
    if (!globalSearch || globalSearch.length < 2) { setGlobalResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        setGlobalSearching(true);
        const { data } = await axios.get(`${API_URL}/api/fabric-collections/search-colors?q=${encodeURIComponent(globalSearch)}&limit=20`);
        setGlobalResults(data.results || []);
      } catch { setGlobalResults([]); }
      finally { setGlobalSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  // Navigate to a search result
  const navigateToColor = useCallback(async (result) => {
    // Find the brand and expand it
    const brand = brands.find(b => b.name === result.brand_name);
    if (brand) {
      setExpandedBrands(prev => ({ ...prev, [brand.id]: true }));
      setSelectedBrand(brand);
      // Fetch collections for that brand to ensure they're loaded
      try {
        const url = `${API_URL}/api/admin/fabric-collections/collections?brand_id=${brand.id}`;
        const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setCollections(data);
        const coll = data.find(c => String(c.id || c._id) === result.collection_id);
        if (coll) {
          setSelectedCollection(coll);
          if (result.material_id) {
            const mat = (coll.materials || []).find(m => m.id === result.material_id);
            if (mat) setSelectedMaterial(mat);
          }
        }
      } catch (err) {
        console.error('Failed to navigate to color:', err);
      }
    }
    setGlobalSearch('');
    setGlobalResults([]);
  }, [brands, token]);

  const lastJump = useRef(null);
  useEffect(() => {
    if (!jumpTo || !brands.length || lastJump.current === jumpTo._t) return;
    lastJump.current = jumpTo._t;
    navigateToColor(jumpTo);
  }, [jumpTo, brands, navigateToColor]);

  // ==================== DATA FETCHING ====================
  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/fabric-collections/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
      toast.error('Failed to load fabric brands');
    }
  }, [token]);

  const fetchCollections = useCallback(async (brandId = null) => {
    try {
      const url = brandId 
        ? `${API_URL}/api/admin/fabric-collections/collections?brand_id=${brandId}`
        : `${API_URL}/api/admin/fabric-collections/collections`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchBrands();
      await fetchCollections();
      setLoading(false);
    };
    loadData();
  }, [fetchBrands, fetchCollections]);

  // ==================== SEED FUNCTIONS ====================
  const seedDW = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/seed-dw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'DW data imported!');
      await fetchBrands();
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to seed DW data');
    } finally {
      setSaving(false);
    }
  };

  const seedCoulisse = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/seed-coulisse`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Coulisse data imported!');
      await fetchBrands();
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to seed Coulisse data');
    } finally {
      setSaving(false);
    }
  };

  // ==================== BRAND CRUD ====================
  const saveBrand = async () => {
    if (!editingBrand?.name?.trim()) {
      toast.error('Brand name is required');
      return;
    }
    try {
      setSaving(true);
      if (editingBrand.id) {
        await axios.put(
          `${API_URL}/api/admin/fabric-collections/brands/${editingBrand.id}`,
          editingBrand,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Brand updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/fabric-collections/brands`,
          editingBrand,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Brand created');
      }
      setBrandModalOpen(false);
      setEditingBrand(null);
      await fetchBrands();
    } catch (error) {
      toast.error('Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async () => {
    if (!deleteTarget?.id) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/fabric-collections/brands/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Brand deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchBrands();
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  // ==================== COLLECTION CRUD ====================
  const saveCollection = async () => {
    if (!editingCollection?.name?.trim()) {
      toast.error('Collection name is required');
      return;
    }
    if (!editingCollection?.brand_id) {
      toast.error('Please select a brand');
      return;
    }
    try {
      setSaving(true);
      if (editingCollection.id) {
        await axios.put(
          `${API_URL}/api/admin/fabric-collections/collections/${editingCollection.id}`,
          editingCollection,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Collection updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/fabric-collections/collections`,
          editingCollection,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Collection created');
      }
      setCollectionModalOpen(false);
      setEditingCollection(null);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to save collection');
    } finally {
      setSaving(false);
    }
  };

  const deleteCollection = async () => {
    if (!deleteTarget?.id) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/fabric-collections/collections/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Collection deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setSelectedCollection(null);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to delete collection');
    }
  };

  // ==================== MATERIAL CRUD ====================
  const saveMaterial = async () => {
    if (!editingMaterial?.name?.trim()) {
      toast.error('Material name is required');
      return;
    }
    if (!selectedCollection?.id) return;
    try {
      setSaving(true);
      if (editingMaterial.id) {
        await axios.put(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${editingMaterial.id}`,
          editingMaterial,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Material updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials`,
          editingMaterial,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Material added');
      }
      setMaterialModalOpen(false);
      setEditingMaterial(null);
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to save material');
    } finally {
      setSaving(false);
    }
  };

  const deleteMaterial = async () => {
    if (!deleteTarget?.id || !selectedCollection?.id) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Material deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setSelectedMaterial(null);
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  // ==================== COLOR CRUD ====================
  const saveColor = async () => {
    if (!editingColor?.name?.trim()) {
      toast.error('Color name is required');
      return;
    }
    if (!selectedCollection?.id) return;
    try {
      setSaving(true);
      let savedColor = null;
      const autoGenParam = editingColor.id ? '' : `?auto_generate_viz=${autoGenerateViz}&room_type=${vizRoomType}`;
      
      if (selectedMaterial?.id) {
        if (editingColor.id) {
          const resp = await axios.put(
            `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${selectedMaterial.id}/colors/${editingColor.id}`,
            editingColor,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          savedColor = resp.data;
        } else {
          const resp = await axios.post(
            `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${selectedMaterial.id}/colors${autoGenParam}`,
            editingColor,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          savedColor = resp.data;
        }
      } else {
        if (editingColor.id) {
          const resp = await axios.put(
            `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/colors/${editingColor.id}`,
            editingColor,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          savedColor = resp.data;
        } else {
          const resp = await axios.post(
            `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/colors${autoGenParam}`,
            editingColor,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          savedColor = resp.data;
        }
      }
      
      toast.success(editingColor.id ? 'Color updated' : 'Color added');
      
      if (savedColor?.viz_job_id) {
        toast.info('AI room visualization is generating in the background', {
          description: 'It will appear in the Fabric Color Gallery in about a minute.'
        });
      }
      
      setColorModalOpen(false);
      setEditingColor(null);
      setAutoGenerateViz(true);
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      if (selectedMaterial?.id) {
        const mat = response.data.materials?.find(m => m.id === selectedMaterial.id);
        setSelectedMaterial(mat || null);
      }
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to save color');
    } finally {
      setSaving(false);
    }
  };

  const deleteColor = async () => {
    if (!deleteTarget?.id || !selectedCollection?.id) return;
    try {
      if (selectedMaterial?.id) {
        await axios.delete(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${selectedMaterial.id}/colors/${deleteTarget.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.delete(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/colors/${deleteTarget.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Color deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      if (selectedMaterial?.id) {
        const mat = response.data.materials?.find(m => m.id === selectedMaterial.id);
        setSelectedMaterial(mat || null);
      }
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to delete color');
    }
  };

  // ==================== REORDER HANDLERS ====================
  const handleMaterialReorder = async (newOrder) => {
    if (!selectedCollection?.id) return;
    setSelectedCollection(prev => ({ ...prev, materials: newOrder }));
    try {
      const orderData = newOrder.map((mat, i) => ({ id: mat.id, order: i }));
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/reorder`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Failed to reorder materials');
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
    }
  };

  const handleColorReorder = async (newOrder) => {
    if (!selectedCollection?.id) return;
    setSelectedCollection(prev => ({ ...prev, colors: newOrder }));
    try {
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/colors/reorder`,
        newOrder.map((c, i) => ({ id: c.id, order: i })),
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Failed to reorder colors');
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
    }
  };

  const handleMaterialColorReorder = async (newOrder) => {
    if (!selectedCollection?.id || !selectedMaterial?.id) return;
    setSelectedMaterial(prev => ({ ...prev, colors: newOrder }));
    try {
      // Save custom_order via the smart reorder endpoint
      const colorOrders = newOrder.map((c, i) => ({ id: c.id, order: i }));
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${selectedMaterial.id}/colors/reorder`,
        colorOrders,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Failed to reorder colors');
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      const mat = response.data.materials?.find(m => m.id === selectedMaterial.id);
      setSelectedMaterial(mat || null);
    }
  };

  // ==================== AUDIT COLORS ====================
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const handleAuditColors = async (collection) => {
    setAuditLoading(true);
    setAuditResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/audit-colors`,
        { collection_id: collection?.id || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuditResult(response.data);
      if (response.data.mismatch_count === 0) {
        toast.success(`Audit complete: All ${response.data.matches} colors match their CDN originals!`);
      } else {
        toast.warning(`Audit found ${response.data.mismatch_count} mismatches out of ${response.data.total_scanned} colors`);
      }
    } catch (error) {
      toast.error('Failed to run color audit');
    } finally {
      setAuditLoading(false);
    }
  };

  // ==================== MATERIAL CATALOG URL ====================
  const handleSaveMaterialCatalogUrl = async (collectionId, materialId, catalogUrl) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${collectionId}/materials/${materialId}/catalog-url`,
        { catalog_url: catalogUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Material catalog link saved');
      // Update local state
      setSelectedCollection(prev => {
        if (!prev) return prev;
        const updated = { ...prev, materials: prev.materials?.map(m => m.id === materialId ? { ...m, catalog_url: catalogUrl } : m) };
        return updated;
      });
      if (selectedMaterial?.id === materialId) {
        setSelectedMaterial(prev => ({ ...prev, catalog_url: catalogUrl }));
      }
    } catch (error) {
      toast.error('Failed to save catalog link');
    }
  };

  // ==================== SMART REORDER (CUSTOM ORDER) ====================
  const handleResetColorOrder = async (collectionId, materialId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${collectionId}/materials/${materialId}/colors/reset-order`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Color order reset to article number sort');
      await fetchCollections();
      await refreshSelectedCollection(true);
    } catch (error) {
      toast.error('Failed to reset color order');
    }
  };

  // ==================== CATALOG RESYNC (LIVE) ====================
  const refreshAfterResync = useCallback(async () => {
    await fetchCollections();
    if (selectedCollection?.id) await refreshSelectedCollection(true);
  }, [fetchCollections, selectedCollection?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { job: resyncJob, start: handleResyncFromCatalog, dismiss: dismissResyncJob } = useCatalogResync(token, refreshAfterResync);

  // ==================== COLOR COMPARISON ====================
  const [compareOpen, setCompareOpen] = useState(false);

  // ==================== CSV IMPORT ====================
  const handleCsvTextChange = (text) => {
    setCsvData(text);
    setCsvPreview(parseColorsCSV(text));
  };

  const handleMaterialCsvTextChange = (text) => {
    setMaterialCsvData(text);
    setMaterialCsvPreview(parseMaterialsCSV(text));
  };

  const refreshSelectedCollection = async (keepMaterial = false) => {
    const { data } = await axios.get(
      `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSelectedCollection(data);
    if (keepMaterial && selectedMaterial?.id) {
      setSelectedMaterial(data.materials?.find(m => m.id === selectedMaterial.id) || null);
    }
  };

  const importColorsFromCSV = async () => {
    if (csvPreview.length === 0) { toast.error('No valid colors to import'); return; }
    try {
      setSaving(true);
      const endpoint = selectedMaterial?.id
        ? `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/${selectedMaterial.id}/colors/bulk`
        : `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/colors/bulk`;
      const response = await axios.post(endpoint, { colors: csvPreview }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Imported ${response.data.imported_count} colors`);
      setCsvImportOpen(false);
      setCsvData('');
      setCsvPreview([]);
      await refreshSelectedCollection(true);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to import colors');
    } finally {
      setSaving(false);
    }
  };

  const importMaterialsFromCSV = async () => {
    if (materialCsvPreview.length === 0) { toast.error('No valid materials to import'); return; }
    if (!selectedCollection?.id) { toast.error('Please select a collection first'); return; }
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/bulk`,
        { materials: materialCsvPreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Imported ${response.data.imported_count} materials`);
      setMaterialImportOpen(false);
      setMaterialCsvData('');
      setMaterialCsvPreview([]);
      await refreshSelectedCollection();
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to import materials');
    } finally {
      setSaving(false);
    }
  };


  // ==================== IMAGE UPLOAD ====================
  const uploadColorImage = async (file) => {
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'fabric-colors');
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.success) {
        setEditingColor(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // ==================== BULK PRICE UPDATE ====================
  const handleBulkPriceUpdate = async () => {
    if (pricePercentage === 0) {
      toast.error('Please enter a percentage');
      return;
    }
    try {
      setSaving(true);
      let endpoint;
      if (priceScope === 'brand' && selectedBrand?.id) {
        endpoint = `${API_URL}/api/admin/fabric-collections/brands/${selectedBrand.id}/bulk-price`;
      } else if (selectedCollection?.id) {
        endpoint = `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/bulk-price`;
      } else {
        toast.error('Please select a collection or brand');
        return;
      }
      const response = await axios.put(endpoint, { percentage: pricePercentage, round_to: 2 }, { headers: { Authorization: `Bearer ${token}` } });
      const change = pricePercentage > 0 ? 'increased' : 'decreased';
      toast.success(`${response.data.materials_updated || response.data.updated_count} material prices ${change} by ${Math.abs(pricePercentage)}%`);
      setBulkPriceOpen(false);
      setPricePercentage(0);
      if (selectedCollection?.id) {
        const collResponse = await axios.get(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedCollection(collResponse.data);
      }
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to update prices');
    } finally {
      setSaving(false);
    }
  };

  const handleFlatPriceUpdate = async (basePriceVal, priceSqftVal) => {
    if (basePriceVal <= 0 || !selectedCollection?.id) {
      toast.error('Please enter a valid price and select a collection');
      return;
    }
    try {
      setSaving(true);
      const payload = { price: basePriceVal };
      if (priceSqftVal > 0) payload.price_per_sqft = priceSqftVal;
      const response = await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/bulk-pricing`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Set pricing for ${response.data.updated_materials} materials`);
      setBulkPriceOpen(false);
      setFlatPrice(0);
      const collResponse = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(collResponse.data);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to set prices');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTier = async (tierId) => {
    if (!tierId || !selectedCollection?.id) return;
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/pricing-tier`,
        { pricing_tier: tierId, apply_to_materials: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Applied ${tierId} tier pricing to ${response.data.updated_materials} materials`);
      setBulkPriceOpen(false);
      const collResponse = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(collResponse.data);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to apply tier pricing');
    } finally {
      setSaving(false);
    }
  };

  // ==================== BULK CSV PRICE UPLOAD ====================
  const handleBulkCsvUpload = async () => {
    if (!bulkCsvFile) { toast.error('Please select a CSV file'); return; }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('file', bulkCsvFile);
      const { data } = await axios.post(
        `${API_URL}/api/admin/fabric-collections/bulk-price-csv`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setBulkCsvResult(data);
      toast.success(`Updated ${data.updated_materials} material prices`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had errors`);
      await fetchCollections();
      if (selectedCollection?.id) {
        const collResp = await axios.get(
          `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedCollection(collResp.data);
      }
    } catch (e) {
      toast.error('CSV upload failed');
    } finally { setSaving(false); }
  };

  // ==================== TEMPLATES ====================
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/fabric-collections/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const createFromTemplate = async () => {
    if (!selectedTemplate || !selectedBrand?.id) {
      toast.error('Please select a brand and template');
      return;
    }
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/fabric-collections/collections/from-template?brand_id=${selectedBrand.id}&template_id=${selectedTemplate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Created "${response.data.name}" with ${response.data.materials_created} materials`);
      setTemplateModalOpen(false);
      setSelectedTemplate(null);
      await fetchCollections();
    } catch (error) {
      toast.error('Failed to create collection from template');
    } finally {
      setSaving(false);
    }
  };

  // ==================== PRICE HISTORY ====================
  const fetchPriceHistory = async (materialId) => {
    if (!materialId) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/price-history/material/${materialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPriceHistory(response.data || []);
    } catch (error) {
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ==================== COMPUTED VALUES ====================
  const filteredMaterials = (selectedCollection?.materials || []).filter(m =>
    m.name.toLowerCase().includes(materialSearch.toLowerCase()) ||
    m.description?.toLowerCase().includes(materialSearch.toLowerCase()) ||
    m.composition?.toLowerCase().includes(materialSearch.toLowerCase())
  );

  const filteredCollections = collections.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const collectionsByBrand = {};
  filteredCollections.forEach(c => {
    if (!collectionsByBrand[c.brand_id]) collectionsByBrand[c.brand_id] = [];
    collectionsByBrand[c.brand_id].push(c);
  });

  // ==================== HANDLERS FOR SUB-COMPONENTS ====================
  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandModalOpen(true);
  };

  const handleDeleteBrand = (brand) => {
    setDeleteTarget({ type: 'brand', ...brand });
    setDeleteDialogOpen(true);
  };
  
  // Handle brand reordering (drag-and-drop)
  const handleReorderBrands = async (newOrder) => {
    // Update local state immediately for smooth UX
    setBrands(newOrder);
    
    // Save order to backend
    try {
      await axios.put(`${API_URL}/api/admin/fabric-collections/brands/reorder`, {
        brand_ids: newOrder.map(b => b.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save brand order:', error);
      toast.error('Failed to save brand order');
      // Revert on error
      fetchBrands();
    }
  };
  
  // Handle collection reordering within a brand (drag-and-drop)
  const handleReorderCollections = async (brandId, newOrder) => {
    // Update local state immediately - replace collections for this brand while keeping others
    setCollections(prev => {
      const otherCollections = prev.filter(c => c.brand_id !== brandId);
      return [...otherCollections, ...newOrder];
    });
    
    // Save order to backend
    try {
      await axios.put(`${API_URL}/api/admin/fabric-collections/collections/reorder`, {
        brand_id: brandId,
        collection_ids: newOrder.map(c => c.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save collection order:', error);
      toast.error('Failed to save collection order');
      // Revert on error
      fetchCollections(brandId);
    }
  };

  const handleAddCollection = (brandId) => {
    setEditingCollection({
      brand_id: brandId,
      name: '',
      description: '',
      fabric_type: '',
      openness: '',
      widths: [],
      colors: [],
      is_active: true
    });
    setCollectionModalOpen(true);
  };

  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
    setCollectionModalOpen(true);
  };

  const handleDeleteCollection = (collection) => {
    setDeleteTarget({ type: 'collection', ...collection });
    setDeleteDialogOpen(true);
  };

  const handleOpenTemplateModal = (brand) => {
    setSelectedBrand(brand);
    fetchTemplates();
    setTemplateModalOpen(true);
  };

  const handleAddMaterial = () => {
    setEditingMaterial({ name: '', description: '', composition: '', weight: '', base_price: null, image_url: '', is_active: true, is_visible_online: true });
    setMaterialModalOpen(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialModalOpen(true);
  };

  const handleDeleteMaterial = (material) => {
    setDeleteTarget({ type: 'material', ...material });
    setDeleteDialogOpen(true);
  };

  const toggleMergeId = (id) => setMergeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const splitMaterial = async (newName) => {
    if (!selectedCollection?.id || !splitReq) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/split`,
        { source_material_id: splitReq.material.id, color_ids: splitReq.colorIds, new_name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Moved ${data.moved} color${data.moved === 1 ? '' : 's'} into "${newName}"`);
      setSplitReq(null);
      const response = await axios.get(`${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedCollection(response.data);
      setSelectedMaterial((response.data.materials || []).find(m => m.id === data.material.id) || null);
      await fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split');
    } finally {
      setSaving(false);
    }
  };

  const bulkSetStock = async ({ material_ids = [], color_ids = [] }, change) => {
    if (!selectedCollection?.id) return;
    const payload = typeof change === 'object' && change !== null ? change : { field: 'stock', stock_status: change };
    try {
      const { data } = await axios.put(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/bulk-stock`,
        { ...payload, material_ids, color_ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const label = payload.field === 'discontinued'
        ? (payload.discontinued ? DISCONTINUED.label : 'Discontinued flag removed')
        : (stockBadge(payload.stock_status)?.label || 'No stock status');
      const L = tierLabels(brands.find(b => b.id === selectedCollection?.brand_id)?.name);
      toast.success(`${label}: ${data.materials ? `${data.materials} ${L.tier3Plural.toLowerCase()}` : ''}${data.materials && data.colors ? ', ' : ''}${data.colors ? `${data.colors} colors` : ''}`);
      if (material_ids.length) setMergeIds([]);
      const response = await axios.get(`${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedCollection(response.data);
      setSelectedMaterial(prev => prev ? (response.data.materials || []).find(m => m.id === prev.id) || null : null);
      await fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const renameLevel = async (level, ids, name) => {
    try {
      await axios.put(`${API_URL}/api/admin/fabric-collections/rename`, { level, name, ...ids }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Renamed to "${name}"`);
      if (level === 'brand') {
        await fetchBrands();
        setSelectedBrand(prev => prev?.id === ids.brand_id ? { ...prev, name } : prev);
      } else {
        const response = await axios.get(`${API_URL}/api/admin/fabric-collections/collections/${ids.collection_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (selectedCollection?.id === ids.collection_id) {
          setSelectedCollection(response.data);
          setSelectedMaterial(prev => prev ? (response.data.materials || []).find(m => m.id === prev.id) || null : null);
        }
      }
      await fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rename failed');
    }
  };

  const mergeMaterials = async (targetName) => {
    if (!selectedCollection?.id || mergeIds.length < 2) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}/materials/merge`,
        { source_ids: mergeIds, target_name: targetName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Merged ${data.merged} into "${targetName}" (${data.colors} colors)`);
      setMergeOpen(false);
      setMergeIds([]);
      setSelectedMaterial(null);
      const response = await axios.get(
        `${API_URL}/api/admin/fabric-collections/collections/${selectedCollection.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCollection(response.data);
      await fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge');
    } finally {
      setSaving(false);
    }
  };

  const handleAddColor = (isMaterialColor) => {
    setEditingColor({ name: '', code: '', image_url: '', hex_color: '', is_active: true, is_visible_online: true });
    setColorModalOpen(true);
  };

  const handleEditColor = (color, isMaterialColor) => {
    setEditingColor(color);
    setColorModalOpen(true);
  };

  const handleDeleteColor = (color) => {
    setDeleteTarget({ type: 'color', ...color });
    setDeleteDialogOpen(true);
  };

  const handleReplaceTexture = (color) => {
    setTextureReplaceColor(color);
    setTextureReplaceOpen(true);
  };

  const handleTextureReplaced = (colorId, newImageUrl) => {
    // Update local state immediately so UI reflects the swap
    if (selectedMaterial) {
      setSelectedMaterial(prev => ({
        ...prev,
        colors: prev.colors?.map(c => c.id === colorId ? { ...c, image_url: newImageUrl, publitas_texture_id: 'yes' } : c)
      }));
    }
    setSelectedCollection(prev => ({
      ...prev,
      colors: prev.colors?.map(c => c.id === colorId ? { ...c, image_url: newImageUrl, publitas_texture_id: 'yes' } : c)
    }));
  };

  const handleOpenBulkPrice = () => {
    setPricePercentage(0);
    setPriceScope('collection');
    setBulkPriceOpen(true);
  };

  const handleOpenMaterialImport = () => {
    setMaterialCsvData('');
    setMaterialCsvPreview([]);
    setMaterialImportOpen(true);
  };

  const handleOpenCsvImport = () => {
    setCsvData('');
    setCsvPreview([]);
    setCsvImportOpen(true);
  };

  const handleOpenImagePreview = (color) => {
    setPreviewImage(color);
    setImagePreviewOpen(true);
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="space-y-6" data-testid="fabric-collections-tab">
      <RegenerateJobBanner job={roomsJob} onDismiss={dismissRoomsJob} />
      <ResyncJobBanner job={resyncJob} onDismiss={dismissResyncJob} />
      <GenerateRoomsDialog
        collection={roomsDialogCollection}
        token={token}
        open={!!roomsDialogCollection}
        onOpenChange={(open) => !open && setRoomsDialogCollection(null)}
        onStarted={adoptJob}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-800">Fabric Collections</h2>
          <p className="text-sm text-neutral-500">Manage brands, collections, materials, and colors</p>
        </div>
        <div className="flex gap-2 items-center">
          <PublicPriceToggle token={token} />
          <Button variant="outline" size="sm" onClick={() => setWeeklyResyncOpen(true)} className="gap-1" data-testid="weekly-resync-btn">
            <CalendarClock className="w-4 h-4" />
            Weekly resync
          </Button>
          <Button variant="outline" size="sm" onClick={() => setStockReportOpen(true)} className="gap-1" data-testid="stock-report-btn">
            <ClipboardList className="w-4 h-4" />
            Stock overview
          </Button>
          <Button variant="outline" size="sm" onClick={seedDW} disabled={saving} className="gap-1">
            <ExternalLink className="w-4 h-4" />
            Import DW
          </Button>
          <Button variant="outline" size="sm" onClick={seedCoulisse} disabled={saving} className="gap-1">
            <ExternalLink className="w-4 h-4" />
            Import Coulisse
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditingBrand({ name: '', website: '', logo_url: '', description: '' });
              setBrandModalOpen(true);
            }}
            className="gap-1 bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4" />
            Add Brand
          </Button>
        </div>
      </div>

      {/* Quick Color Search */}
      <div className="relative" ref={globalSearchRef} data-testid="global-color-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
        <Input
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          placeholder="Quick search — find any color by name or article code..."
          className="pl-9 h-9 bg-neutral-50 border-neutral-200 focus:bg-white"
          data-testid="global-color-search-input"
        />
        {globalSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />}
        {globalResults.length > 0 && (
          <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-lg shadow-xl max-h-72 overflow-y-auto" data-testid="global-color-results">
            {globalResults.map((r, i) => (
              <button
                key={`${r.id}-${i}`}
                onClick={() => navigateToColor(r)}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-amber-50 text-left border-b border-neutral-100 last:border-0 transition-colors"
                data-testid={`search-result-${r.id}`}
              >
                <div className="w-7 h-7 rounded border border-neutral-200 overflow-hidden flex-shrink-0">
                  {r.image_url ? <ColorImage src={r.image_url} alt="" w={128} eager className="w-full h-full" /> : <div className="w-full h-full" style={{ backgroundColor: r.hex_color || '#e5e5e5' }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-neutral-900 truncate">{r.name}</p>
                  <p className="text-[10px] text-neutral-500 truncate">
                    {r.brand_name} · {r.collection_name}{r.material_name ? ` · ${r.material_name}` : ''}
                    {r.code ? ` — ${r.code}` : ''}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Grid - Uses Sub-Components */}
      <div className="grid grid-cols-12 gap-6">
        <BrandsTreePanel
          onRename={renameLevel}
          brands={brands}
          collectionsByBrand={collectionsByBrand}
          expandedBrands={expandedBrands}
          setExpandedBrands={setExpandedBrands}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
          selectedCollection={selectedCollection}
          setSelectedCollection={setSelectedCollection}
          setSelectedMaterial={setSelectedMaterial}
          onEditBrand={handleEditBrand}
          onDeleteBrand={handleDeleteBrand}
          onAddCollection={handleAddCollection}
          onOpenTemplateModal={handleOpenTemplateModal}
          onReorderBrands={handleReorderBrands}
          onReorderCollections={handleReorderCollections}
        />

        <CollectionPanel
          onRename={renameLevel}
          selectedCollection={selectedCollection}
          selectedMaterial={selectedMaterial}
          setSelectedMaterial={setSelectedMaterial}
          filteredMaterials={filteredMaterials}
          materialSearch={materialSearch}
          setMaterialSearch={setMaterialSearch}
          onEditCollection={handleEditCollection}
          onGenerateRooms={setRoomsDialogCollection}
          onDeleteCollection={handleDeleteCollection}
          onOpenBulkPrice={handleOpenBulkPrice}
          onOpenBulkCsv={() => { setBulkCsvFile(null); setBulkCsvResult(null); setBulkCsvOpen(true); }}
          onAddMaterial={handleAddMaterial}
          onEditMaterial={handleEditMaterial}
          onDeleteMaterial={handleDeleteMaterial}
          mergeIds={mergeIds}
          onToggleMerge={toggleMergeId}
          onClearMerge={() => setMergeIds([])}
          onOpenMerge={() => setMergeOpen(true)}
          onBulkStock={bulkSetStock}
          onSplit={(material, colorIds) => setSplitReq({ material, colorIds })}
          onMaterialReorder={handleMaterialReorder}
          onOpenMaterialImport={handleOpenMaterialImport}
          onAddColor={handleAddColor}
          onEditColor={handleEditColor}
          onDeleteColor={handleDeleteColor}
          onColorReorder={handleColorReorder}
          onMaterialColorReorder={handleMaterialColorReorder}
          onOpenCsvImport={handleOpenCsvImport}
          onOpenImagePreview={handleOpenImagePreview}
          onReplaceTexture={handleReplaceTexture}
          onBulkMatch={() => setBulkMatchOpen(true)}
          onAuditColors={handleAuditColors}
          onSaveMaterialCatalogUrl={handleSaveMaterialCatalogUrl}
          onResetColorOrder={handleResetColorOrder}
          onResyncFromCatalog={handleResyncFromCatalog}
          onCompareColors={() => setCompareOpen(true)}
          brandName={brands.find(b => b.id === selectedCollection?.brand_id)?.name}
          token={token}
        />
      </div>

      {/* ==================== MODALS ==================== */}
      <BrandDialog open={brandModalOpen} onOpenChange={setBrandModalOpen} brand={editingBrand} setBrand={setEditingBrand} onSave={saveBrand} saving={saving} />
      <CollectionDialog open={collectionModalOpen} onOpenChange={setCollectionModalOpen} collection={editingCollection} setCollection={setEditingCollection} brands={brands} onSave={saveCollection} saving={saving} />
      <ColorDialog
        open={colorModalOpen} onOpenChange={setColorModalOpen} color={editingColor} setColor={setEditingColor} onSave={saveColor} saving={saving}
        uploadingImage={uploadingImage} onUploadImage={uploadColorImage}
        autoGenerateViz={autoGenerateViz} setAutoGenerateViz={setAutoGenerateViz} vizRoomType={vizRoomType} setVizRoomType={setVizRoomType} generatingViz={generatingViz}
      />
      <MaterialDialog open={materialModalOpen} onOpenChange={setMaterialModalOpen} material={editingMaterial} setMaterial={setEditingMaterial} onSave={saveMaterial} saving={saving} brandName={brands.find(b => b.id === selectedCollection?.brand_id)?.name} />
      <MergeMaterialsDialog
        open={mergeOpen} onOpenChange={setMergeOpen} saving={saving} onMerge={mergeMaterials}
        materials={(selectedCollection?.materials || []).filter(m => mergeIds.includes(m.id))}
        brandName={brands.find(b => b.id === selectedCollection?.brand_id)?.name}
      />
      <SplitMaterialDialog
        open={!!splitReq} onOpenChange={(o) => !o && setSplitReq(null)} saving={saving} onSplit={splitMaterial}
        material={splitReq?.material}
        colors={(splitReq?.material?.colors || []).filter(c => splitReq?.colorIds.includes(c.id))}
        brandName={brands.find(b => b.id === selectedCollection?.brand_id)?.name}
      />
      <WeeklyResyncDialog open={weeklyResyncOpen} onOpenChange={setWeeklyResyncOpen} token={token} />
      <StockReportDialog
        open={stockReportOpen} onOpenChange={setStockReportOpen} token={token}
        onJump={(g) => { setStockReportOpen(false); navigateToColor({ brand_name: g.brand_name, collection_id: g.collection_id, material_id: g.material_id }); }}
      />
      <ImagePreviewDialog open={imagePreviewOpen} onOpenChange={setImagePreviewOpen} image={previewImage} />
      <DeleteConfirmDialog
        open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} target={deleteTarget}
        onConfirm={() => ({ brand: deleteBrand, collection: deleteCollection, material: deleteMaterial, color: deleteColor })[deleteTarget?.type]?.()}
      />
      <CsvImportDialog
        open={csvImportOpen} onOpenChange={setCsvImportOpen} title="Import Colors from CSV" entityLabel="Colors" testId="csv-import-dialog"
        columnsHint="name, hex_color/hex, code, image_url/image, is_on_sale/sale"
        placeholder={'name,hex_color,code\nWhite,#FFFFFF,001\n...'}
        csvText={csvData} onTextChange={handleCsvTextChange} preview={csvPreview} onImport={importColorsFromCSV} saving={saving}
        renderPreviewRow={(c) => (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-4 h-4 rounded border" style={{ backgroundColor: c.hex_color || '#ccc' }} />
            <span>{c.name}</span>
            {c.code && <span className="text-neutral-400">({c.code})</span>}
          </div>
        )}
      />
      <CsvImportDialog
        open={materialImportOpen} onOpenChange={setMaterialImportOpen} title="Import Materials from CSV" entityLabel="Materials" testId="material-csv-import-dialog"
        columnsHint="name, description, composition, weight, base_price/price, image_url/image"
        placeholder={'name,description,composition,base_price\nAspen,Light filtering,100% Polyester,2.50\n...'}
        csvText={materialCsvData} onTextChange={handleMaterialCsvTextChange} preview={materialCsvPreview} onImport={importMaterialsFromCSV} saving={saving}
        renderPreviewRow={(m) => (
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">{m.name}</span>
            <span className="text-neutral-500">{m.composition}</span>
            {m.base_price && <span className="text-green-600">${m.base_price}{m.price_per_sqft ? ` + $${m.price_per_sqft}/sqft` : ''}</span>}
          </div>
        )}
      />
      <BulkPriceDialog
        open={bulkPriceOpen} onOpenChange={setBulkPriceOpen} mode={priceMode} setMode={setPriceMode} scope={priceScope}
        percentage={pricePercentage} setPercentage={setPricePercentage} flatPrice={flatPrice} setFlatPrice={setFlatPrice}
        materialCount={selectedCollection?.materials?.length || 0} onApplyPercentage={handleBulkPriceUpdate} onApplyFlat={handleFlatPriceUpdate} saving={saving}
        collectionPricingTier={selectedCollection?.pricing_tier} onApplyTier={handleApplyTier}
      />
      <BulkCsvPriceDialog
        open={bulkCsvOpen} onOpenChange={setBulkCsvOpen} file={bulkCsvFile} result={bulkCsvResult} saving={saving}
        onFileChange={(f) => { setBulkCsvFile(f); setBulkCsvResult(null); }} onUpload={handleBulkCsvUpload}
      />
      <TemplateDialog open={templateModalOpen} onOpenChange={setTemplateModalOpen} templates={templates} selected={selectedTemplate} onSelect={setSelectedTemplate} onCreate={createFromTemplate} saving={saving} />
      <PriceHistoryDialog open={priceHistoryOpen} onOpenChange={setPriceHistoryOpen} history={priceHistory} loading={loadingHistory} />
      <TextureReplacePicker
        open={textureReplaceOpen} onOpenChange={setTextureReplaceOpen} color={textureReplaceColor}
        collectionId={selectedCollection?.id || selectedCollection?._id} token={token} onReplaced={handleTextureReplaced}
      />
      <BulkMatchDialog
        open={bulkMatchOpen} onOpenChange={setBulkMatchOpen}
        collectionId={selectedCollection?.id || selectedCollection?._id} collectionName={selectedCollection?.name}
        token={token} onApplied={fetchCollections}
      />
      <AuditResultsDialog result={auditResult} onClose={() => setAuditResult(null)} />
      <ColorCompareDialog open={compareOpen} onOpenChange={setCompareOpen} collection={selectedCollection} token={token} />
    </div>
  );
};

export default FabricCollectionsTab;
